/**
 * Admin Offers Page
 *
 * Displays list of all collection offers with status filters, buyer search,
 * sortable columns, pagination, and a detail modal. Shows stats cards for quick overview.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';
import { is3DModelUrl } from '../utils/media';

// ==================== API ====================

const API_BASE = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

async function fetchWithAuth(endpoint: string) {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ==================== Types ====================

interface OfferListItem {
  id: string;
  buyer_address: string;
  collection_id: string;
  issuer_address: string;
  taxon: number;
  display_amount_drops: string;
  escrow_amount_drops: string;
  seller_receives_drops: string;
  status: string;
  trait_filters: Record<string, unknown> | null;
  has_trait_filters: boolean;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  filled_nft_token_id: string | null;
  filled_by_address: string | null;
  filled_at: string | null;
  buyer_username: string | null;
  collection_name: string | null;
  collection_image_url: string | null;
  collection_slug: string | null;
}

interface OfferStats {
  statusCounts: {
    active: number;
    filled: number;
    cancelled: number;
    expired: number;
    pending: number;
  };
  totalValueLockedXrp: number;
  activity24h: {
    created: number;
    filled: number;
    cancelled: number;
  };
  topCollections: Array<{
    collectionId: string;
    collectionName: string | null;
    collectionImageUrl: string | null;
    collectionSlug: string | null;
    activeOfferCount: number;
    totalValueXrp: number;
  }>;
  traitFilterCount: number;
}

interface OfferListResponse {
  offers: OfferListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  statusCounts: Record<string, number>;
}

interface OfferDetail {
  offer: {
    id: string;
    buyer_address: string;
    collection_id: string;
    issuer_address: string;
    taxon: number;
    display_amount_drops: string;
    escrow_amount_drops: string;
    seller_receives_drops: string;
    escrow_sequence: number | null;
    escrow_ledger_index: number | null;
    condition: string | null;
    cancel_after: string | null;
    expires_at: string | null;
    status: string;
    trait_filters: Record<string, unknown> | null;
    filled_nft_token_id: string | null;
    filled_by_address: string | null;
    filled_at: string | null;
    fill_transaction_hash: string | null;
    cancelled_at: string | null;
    cancel_transaction_hash: string | null;
    cancel_reason: string | null;
    escrow_create_transaction_hash: string | null;
    created_at: string;
    updated_at: string;
    buyer_username: string | null;
  };
  collection: {
    id: string;
    name: string;
    slug: string;
    image_url: string | null;
    creator_address: string;
  } | null;
  filledNft: {
    nft_token_id: string;
    name: string | null;
    image_url: string | null;
    thumbnail_url: string | null;
    rarity_rank: number | null;
    collection_id: string;
  } | null;
  fillerUser: {
    xrpl_address: string;
    username: string | null;
  } | null;
  buyerOfferCount: number;
}

type SortField = 'created_at' | 'display_amount_drops' | 'expires_at';

interface StatusTab {
  key: string;
  label: string;
  statuses: string;
}

const STATUS_TABS: StatusTab[] = [
  { key: 'all', label: 'All', statuses: '' },
  { key: 'active', label: 'Active', statuses: 'active' },
  { key: 'pending', label: 'Pending', statuses: 'pending' },
  { key: 'filled', label: 'Filled', statuses: 'filled' },
  { key: 'inactive', label: 'Cancelled/Expired', statuses: 'cancelled,expired' },
];

// ==================== Helpers ====================

function formatXrp(drops: string | number | null | undefined): string {
  if (drops === null || drops === undefined || drops === '0') return '-';
  return `${(Number(drops) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} XRP`;
}

function formatXrpFull(drops: string | number | null | undefined): string {
  if (drops === null || drops === undefined || drops === '0') return '-';
  return `${(Number(drops) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 })} XRP`;
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateFull(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function truncateId(id: string): string {
  return `${id.slice(0, 8)}...`;
}

function truncateAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

function getOfferStatusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-700';
    case 'filled':
      return 'bg-blue-100 text-blue-700';
    case 'cancelled':
      return 'bg-red-100 text-red-700';
    case 'expired':
      return 'bg-gray-100 text-gray-600';
    case 'pending':
      return 'bg-yellow-100 text-yellow-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

// ==================== Sort Header Component ====================

function SortHeader({
  label,
  field,
  currentSort,
  currentOrder,
  onSort,
}: {
  label: string;
  field: SortField;
  currentSort: SortField;
  currentOrder: 'asc' | 'desc';
  onSort: (field: SortField) => void;
}) {
  const isActive = currentSort === field;
  return (
    <th
      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:text-gray-700 select-none"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {isActive && (
          <span className="text-blue-600">
            {currentOrder === 'asc' ? '\u2191' : '\u2193'}
          </span>
        )}
      </div>
    </th>
  );
}

// ==================== Detail Modal Component ====================

function OfferDetailModal({
  offerId,
  onClose,
}: {
  offerId: string;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<OfferDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchWithAuth(`/admin/offers/${offerId}`);
        if (!cancelled) {
          if (result.success) {
            setDetail(result.data);
          } else {
            throw new Error(result.error || 'Failed to load offer detail');
          }
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to load offer detail');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [offerId]);

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const CopyButton = ({ text, field }: { text: string; field: string }) => (
    <button
      onClick={() => handleCopy(text, field)}
      className="ml-1 text-gray-400 hover:text-gray-600 inline-flex"
      title="Copy"
    >
      {copiedField === field ? (
        <svg className="h-3.5 w-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
      ) : (
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
      )}
    </button>
  );

  const InfoRow = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
    <div className="flex justify-between py-1.5">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm text-gray-900 text-right ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 pt-16">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/40" onClick={onClose} />

        {/* Modal */}
        <div className="relative w-full max-w-2xl rounded-lg bg-white shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">Offer Detail</h2>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                  <p className="mt-4 text-sm text-gray-600">Loading offer detail...</p>
                </div>
              </div>
            )}

            {error && (
              <ErrorDisplay error={error} />
            )}

            {detail && (
              <div className="space-y-6">
                {/* Status & ID */}
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getOfferStatusColor(detail.offer.status)}`}>
                      {detail.offer.status}
                    </span>
                    <span className="text-sm font-mono text-gray-500">
                      {detail.offer.id}
                    </span>
                    <CopyButton text={detail.offer.id} field="offer-id" />
                  </div>
                </div>

                {/* Buyer Info */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Buyer</h3>
                  <InfoRow
                    label="Username"
                    value={detail.offer.buyer_username || 'No username'}
                  />
                  <InfoRow
                    label="Address"
                    value={
                      <span className="flex items-center gap-1">
                        <span className="font-mono">{truncateAddress(detail.offer.buyer_address)}</span>
                        <CopyButton text={detail.offer.buyer_address} field="buyer-addr" />
                      </span>
                    }
                  />
                  <InfoRow label="Total Offers" value={detail.buyerOfferCount} />
                </div>

                {/* Collection Info */}
                {detail.collection && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Collection</h3>
                    <div className="flex items-center gap-3 mb-3">
                      {detail.collection.image_url && (
                        <img
                          src={detail.collection.image_url}
                          alt=""
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {detail.collection.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          /{detail.collection.slug}
                        </div>
                      </div>
                    </div>
                    <InfoRow
                      label="Creator"
                      value={
                        <span className="flex items-center gap-1">
                          <span className="font-mono">{truncateAddress(detail.collection.creator_address)}</span>
                          <CopyButton text={detail.collection.creator_address} field="creator-addr" />
                        </span>
                      }
                    />
                    <InfoRow label="Taxon" value={detail.offer.taxon} mono />
                  </div>
                )}

                {/* Amount Breakdown */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Amount Breakdown</h3>
                  <InfoRow label="Display Amount" value={formatXrpFull(detail.offer.display_amount_drops)} />
                  <InfoRow label="Escrow Amount" value={formatXrpFull(detail.offer.escrow_amount_drops)} />
                  <InfoRow label="Seller Receives" value={formatXrpFull(detail.offer.seller_receives_drops)} />
                  {detail.offer.escrow_sequence && (
                    <>
                      <div className="my-2 border-t border-gray-200" />
                      <InfoRow label="Escrow Sequence" value={detail.offer.escrow_sequence} mono />
                      {detail.offer.escrow_ledger_index && (
                        <InfoRow label="Escrow Ledger Index" value={detail.offer.escrow_ledger_index} mono />
                      )}
                      {detail.offer.condition && (
                        <InfoRow
                          label="Condition"
                          value={
                            <span className="max-w-[200px] truncate inline-block font-mono text-xs">
                              {detail.offer.condition}
                            </span>
                          }
                        />
                      )}
                    </>
                  )}
                  {detail.offer.escrow_create_transaction_hash && (
                    <InfoRow
                      label="Escrow TX"
                      value={
                        <a
                          href={`https://bithomp.com/en/tx/${encodeURIComponent(detail.offer.escrow_create_transaction_hash)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 font-mono text-xs"
                        >
                          {detail.offer.escrow_create_transaction_hash.slice(0, 12)}...
                        </a>
                      }
                    />
                  )}
                </div>

                {/* Trait Filters */}
                {detail.offer.trait_filters && Object.keys(detail.offer.trait_filters).length > 0 && (
                  <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                    <h3 className="text-sm font-semibold text-purple-700 mb-3">Trait Filters</h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(detail.offer.trait_filters).map(([traitType, traitValue]) => (
                        <span
                          key={traitType}
                          className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-800"
                        >
                          {traitType}: {Array.isArray(traitValue) ? traitValue.join(', ') : String(traitValue)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Filled Details */}
                {detail.offer.status === 'filled' && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <h3 className="text-sm font-semibold text-blue-700 mb-3">Fill Details</h3>
                    <InfoRow label="Filled At" value={formatDateFull(detail.offer.filled_at)} />
                    {detail.fillerUser && (
                      <InfoRow
                        label="Filled By"
                        value={
                          <span>
                            {detail.fillerUser.username || truncateAddress(detail.fillerUser.xrpl_address)}
                          </span>
                        }
                      />
                    )}
                    {detail.offer.fill_transaction_hash && (
                      <InfoRow
                        label="Fill TX"
                        value={
                          <a
                            href={`https://bithomp.com/en/tx/${encodeURIComponent(detail.offer.fill_transaction_hash)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 font-mono text-xs"
                          >
                            {detail.offer.fill_transaction_hash.slice(0, 12)}...
                          </a>
                        }
                      />
                    )}
                    {detail.filledNft && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <div className="flex items-start gap-3">
                          {is3DModelUrl(detail.filledNft.image_url) ? (
                            <div className="h-14 w-14 rounded-md flex items-center justify-center bg-neutral-100 flex-shrink-0">
                              <span style={{ fontSize: '24px' }}>🧊</span>
                            </div>
                          ) : (detail.filledNft.thumbnail_url || detail.filledNft.image_url) ? (
                            <img
                              src={detail.filledNft.thumbnail_url || detail.filledNft.image_url || ''}
                              alt={detail.filledNft.name || 'NFT'}
                              className="h-14 w-14 rounded-md object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="h-14 w-14 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs text-blue-400">No img</span>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {detail.filledNft.name || 'Unnamed NFT'}
                            </div>
                            {detail.filledNft.rarity_rank && (
                              <span className="inline-flex rounded bg-purple-50 px-1.5 py-0.5 text-xs text-purple-700 mt-1">
                                Rank #{detail.filledNft.rarity_rank}
                              </span>
                            )}
                            <div className="mt-1 text-xs font-mono text-gray-400 truncate">
                              {detail.filledNft.nft_token_id.slice(0, 20)}...
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Cancelled Details */}
                {detail.offer.status === 'cancelled' && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                    <h3 className="text-sm font-semibold text-red-700 mb-3">Cancel Details</h3>
                    <InfoRow label="Cancelled At" value={formatDateFull(detail.offer.cancelled_at)} />
                    <InfoRow label="Reason" value={detail.offer.cancel_reason || 'No reason provided'} />
                    {detail.offer.cancel_transaction_hash && (
                      <InfoRow
                        label="Cancel TX"
                        value={
                          <a
                            href={`https://bithomp.com/en/tx/${encodeURIComponent(detail.offer.cancel_transaction_hash)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-red-600 hover:text-red-800 font-mono text-xs"
                          >
                            {detail.offer.cancel_transaction_hash.slice(0, 12)}...
                          </a>
                        }
                      />
                    )}
                  </div>
                )}

                {/* Timestamps */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Timestamps</h3>
                  <InfoRow label="Created" value={formatDateFull(detail.offer.created_at)} />
                  <InfoRow label="Updated" value={formatDateFull(detail.offer.updated_at)} />
                  <InfoRow label="Expires" value={formatDateFull(detail.offer.expires_at)} />
                  {detail.offer.cancel_after && (
                    <InfoRow label="Cancel After" value={formatDateFull(detail.offer.cancel_after)} />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end border-t border-gray-200 px-6 py-3">
            <button
              onClick={onClose}
              className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== Main Component ====================

export function AdminOffersPage() {
  const [data, setData] = useState<OfferListResponse | null>(null);
  const [stats, setStats] = useState<OfferStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [buyerInput, setBuyerInput] = useState('');
  const [buyerFilter, setBuyerFilter] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const tab = STATUS_TABS.find((t) => t.key === activeTab);
      const statusParam = tab?.statuses || undefined;

      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '50');
      if (statusParam) params.set('status', statusParam);
      if (buyerFilter) params.set('buyer', buyerFilter);
      params.set('sort', sortField);
      params.set('order', sortOrder);

      const query = params.toString();
      const result = await fetchWithAuth(`/admin/offers${query ? `?${query}` : ''}`);
      if (result.success) {
        setData(result.data);
      } else {
        throw new Error(result.error || 'Failed to load offers');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load offers');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, sortField, sortOrder, buyerFilter]);

  const loadStats = useCallback(async () => {
    try {
      const result = await fetchWithAuth('/admin/offers/stats');
      if (result.success) {
        setStats(result.data);
      }
    } catch {
      // Stats failure is non-critical
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
    setPage(1);
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleBuyerSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setBuyerFilter(buyerInput.trim());
    setPage(1);
  };

  const handleClearBuyer = () => {
    setBuyerInput('');
    setBuyerFilter('');
    setPage(1);
  };

  const handleCopyId = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const offers = data?.offers || [];
  const statusCounts = data?.statusCounts || {};
  const pagination = data?.pagination;

  // Calculate tab counts
  const getTabCount = (tab: StatusTab): number => {
    if (tab.key === 'all') {
      return Object.values(statusCounts).reduce((a, b) => a + b, 0);
    }
    const statuses = tab.statuses.split(',');
    return statuses.reduce((sum, s) => sum + (statusCounts[s] || 0), 0);
  };

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Collection Offers</h1>
            <p className="mt-2 text-sm text-gray-600">
              Monitor escrow-based collection buy offers
            </p>
          </div>
          <button
            onClick={() => { loadData(); loadStats(); }}
            disabled={loading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="mb-6 grid gap-4 sm:grid-cols-5">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-green-600">Active Offers</div>
              <div className="mt-1 text-2xl font-bold text-green-700">{stats.statusCounts.active}</div>
            </div>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-blue-600">Filled (All-time)</div>
              <div className="mt-1 text-2xl font-bold text-blue-700">{stats.statusCounts.filled}</div>
            </div>
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-purple-600">Value Locked</div>
              <div className="mt-1 text-2xl font-bold text-purple-700">
                {stats.totalValueLockedXrp.toLocaleString(undefined, { maximumFractionDigits: 0 })} XRP
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-medium text-gray-500">24h Activity</div>
              <div className="mt-1 text-2xl font-bold text-gray-900">{stats.activity24h.created}</div>
              <div className="mt-0.5 text-xs text-gray-400">
                {stats.activity24h.filled} filled, {stats.activity24h.cancelled} cancelled
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-medium text-gray-500">Trait Filtered</div>
              <div className="mt-1 text-2xl font-bold text-gray-900">{stats.traitFilterCount}</div>
            </div>
          </div>
        )}

        {/* Top Collections (compact) */}
        {stats && stats.topCollections.length > 0 && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Collections by Active Offers</h3>
            <div className="flex flex-wrap gap-3">
              {stats.topCollections.map((tc) => (
                <div
                  key={tc.collectionId}
                  className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                >
                  {tc.collectionImageUrl && (
                    <img
                      src={tc.collectionImageUrl}
                      alt=""
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {tc.collectionName || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {tc.activeOfferCount} offers / {tc.totalValueXrp.toLocaleString(undefined, { maximumFractionDigits: 0 })} XRP
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter Row: Tabs + Buyer Search */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 pb-4">
          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((tab) => {
              const count = getTabCount(tab);
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${
                      isActive ? 'bg-blue-200' : 'bg-gray-200'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <form onSubmit={handleBuyerSearch} className="flex items-center gap-2">
            <input
              type="text"
              value={buyerInput}
              onChange={(e) => setBuyerInput(e.target.value)}
              placeholder="Filter by buyer address..."
              className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="submit"
              className="rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Search
            </button>
            {buyerFilter && (
              <button
                type="button"
                onClick={handleClearBuyer}
                className="rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-200"
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {/* Error State */}
        {error && (
          <ErrorDisplay error={error} onRetry={loadData} className="mb-6" />
        )}

        {/* Loading State */}
        {loading && offers.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-sm text-gray-600">Loading offers...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && offers.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
            <p className="text-gray-500">
              {buyerFilter
                ? `No offers found for buyer ${truncateAddress(buyerFilter)}.`
                : activeTab === 'all'
                  ? 'No offers found.'
                  : `No ${activeTab} offers found.`}
            </p>
          </div>
        )}

        {/* Offers Table */}
        {offers.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Offer ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Buyer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Collection
                    </th>
                    <SortHeader label="Amount" field="display_amount_drops" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Traits
                    </th>
                    <SortHeader label="Expires" field="expires_at" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                    <SortHeader label="Created" field="created_at" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {offers.map((offer) => (
                    <tr
                      key={offer.id}
                      onClick={() => setSelectedOfferId(offer.id)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-mono text-gray-600">
                            {truncateId(offer.id)}
                          </span>
                          <button
                            onClick={(e) => handleCopyId(offer.id, e)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Copy offer ID"
                          >
                            {copiedId === offer.id ? (
                              <svg className="h-3.5 w-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            ) : (
                              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {offer.buyer_username || truncateAddress(offer.buyer_address)}
                        </div>
                        {offer.buyer_username && (
                          <div className="text-xs text-gray-500 font-mono">
                            {truncateAddress(offer.buyer_address)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {offer.collection_image_url && (
                            <img
                              src={offer.collection_image_url}
                              alt=""
                              className="h-6 w-6 rounded-full object-cover"
                            />
                          )}
                          <span className="text-sm text-gray-900 truncate max-w-[150px]">
                            {offer.collection_name || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatXrp(offer.display_amount_drops)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getOfferStatusColor(offer.status)}`}>
                          {offer.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {offer.has_trait_filters ? (
                          <span className="inline-flex rounded bg-purple-50 px-1.5 py-0.5 text-xs text-purple-700">
                            Yes
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(offer.expires_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(offer.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-3">
                <div className="text-sm text-gray-700">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} offers
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(1)}
                    disabled={pagination.page <= 1}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    First
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.page <= 1}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setPage(pagination.totalPages)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Detail Modal */}
        {selectedOfferId && (
          <OfferDetailModal
            offerId={selectedOfferId}
            onClose={() => setSelectedOfferId(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}
